// useTonDeposit.js - Hook for handling TON deposit transactions
import { useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';
import { beginCell } from 'ton';
import { ID } from 'appwrite';
import { createDepositRecord, updateDepositRecordStatus } from '../services/appwrite';
import { useContext } from 'react';
import { TelegramUserContext } from '../App';

export const useTonDeposit = () => {
  const [tonConnectUI] = useTonConnectUI();
  const userAddress = useTonAddress();
  const telegramUser = useContext(TelegramUserContext);
  // This is where all TON payments will be received
  // Make sure this is a TESTNET address when testing!
  const DEPOSIT_ADDRESS = "0QCsEG8VkGtHo9pp42yCwOvCA0qtrzoXjX8umpRd03-mfRWp"; // Your testnet address

  const sendDepositTransaction = async (item) => {
    const { testnetTon: tonAmount, amount: coinAmount } = item;
    if (!userAddress) {
      // Open TON Connect modal if wallet is not connected
      tonConnectUI.openModal();
      return { success: false, error: 'Wallet not connected' };
    }

    try {
      const transactionId = ID.unique();
      const nanotons = Math.floor(tonAmount * 1000000000);

      // Create a pending record in the database first
      await createDepositRecord(transactionId, telegramUser.id, coinAmount, tonAmount);

      // Create a cell with the transaction ID as a comment using the builder pattern
      const body = beginCell()
        .storeUint(0, 32) // op code for a text comment
        .storeStringTail(transactionId) // store the comment
        .endCell();
      const payload = body.toBoc().toString('base64'); // Convert the cell to a base64 string

      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [
          {
            address: DEPOSIT_ADDRESS,
            amount: nanotons.toString(),
            payload: payload,
          },
        ],
      };

      try {
        const result = await tonConnectUI.sendTransaction(transaction);
        // If successful, update the record
        await updateDepositRecordStatus(transactionId, 'completed');
        return { success: true, result };
      } catch (error) {
        // If failed, update the record with the error
        const errorMessage = error.message || 'Failed to send transaction';
        await updateDepositRecordStatus(transactionId, 'failed', errorMessage);
        throw error; // Re-throw the error to be caught by the calling component
      }
      
    } catch (error) {
      // Handle different types of errors
      if (error.message && error.message.includes('User declined')) {
        return {
          success: false,
          error: 'Transaction was cancelled by user'
        };
      }
      
      if (error.message && error.message.includes('Insufficient funds')) {
        return {
          success: false,
          error: 'Insufficient funds in wallet'
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to send transaction'
      };
    }
  };

  return {
    sendDepositTransaction,
    isConnected: !!userAddress,
    userAddress,
    openConnectModal: () => tonConnectUI.openModal()
  };
};
